import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '../../utils/cn'

/**
 * JPG Academy Content Tabs
 *
 * Motion is built in and respects prefers-reduced-motion.
 */

interface TabsContextValue {
	value: string
	onValueChange: (value: string) => void
}
const TabsContext = React.createContext<TabsContextValue>({ value: '', onValueChange: () => {} })

export interface TabsProps {
	value?: string
	defaultValue?: string
	onValueChange?: (value: string) => void
	children: React.ReactNode
	className?: string
}

export function Tabs({ value: controlledValue, defaultValue = '', onValueChange, children, className }: TabsProps) {
	const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue)
	const isControlled = controlledValue !== undefined
	const value = isControlled ? controlledValue : uncontrolledValue
	const handleValueChange = React.useCallback((newValue: string) => { if (!isControlled) setUncontrolledValue(newValue); onValueChange?.(newValue) }, [isControlled, onValueChange])
	return <TabsContext.Provider value={{ value, onValueChange: handleValueChange }}><div className={cn('w-full', className)}>{children}</div></TabsContext.Provider>
}

export interface TabsListProps {
	variant?: 'underline' | 'pills'
	className?: string
	children?: React.ReactNode
}export function TabsList({ variant = 'underline', className, ...props }: TabsListProps) {
	return <div role="tablist" className={cn('flex', variant === 'underline' && 'border-b-2 border-navy-200 gap-0', variant === 'pills' && 'gap-1 bg-muted rounded-lg p-1', className)} {...props} />
}

export interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	value: string
	variant?: 'underline' | 'pills'
}export function TabsTrigger({ value, variant = 'underline', className, ...props }: TabsTriggerProps) {
	const { value: activeValue, onValueChange } = React.useContext(TabsContext)
	const isActive = activeValue === value
	return (
		<button
			type="button"
			role="tab"
			aria-selected={isActive}
			tabIndex={isActive ? 0 : -1}
			onClick={() => onValueChange(value)}
			onKeyDown={(e) => {
				if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
					const list = e.currentTarget.parentElement
					if (!list) return
					const triggers = Array.from(list.querySelectorAll<HTMLButtonElement>('[role="tab"]'))
					const idx = triggers.indexOf(e.currentTarget)
					let next: number
					if (e.key === 'ArrowRight') next = (idx + 1) % triggers.length
					else if (e.key === 'ArrowLeft') next = (idx - 1 + triggers.length) % triggers.length
					else if (e.key === 'Home') next = 0
					else next = triggers.length - 1
					triggers[next]?.focus(); triggers[next]?.click(); e.preventDefault()
				}
			}}
			className={cn('relative font-ui font-medium transition-colors duration-fast ease-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/30 flex-shrink-0', variant === 'underline' && ['px-4 pb-2.5 pt-2 text-sm -mb-[2px]', isActive ? 'text-navy-900 font-semibold' : 'text-muted-foreground hover:text-navy-700'], variant === 'pills' && ['px-3 py-1.5 text-sm rounded-md', isActive ? 'text-navy-900 font-semibold' : 'text-muted-foreground hover:text-navy-700'], className)}
			{...props}
		>
			{props.children}
			{variant === 'underline' && isActive && <motion.span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold rounded-full" layoutId="tab-underline" transition={{ type: 'spring', stiffness: 300, damping: 30 }} />}
			{variant === 'pills' && isActive && <motion.span className="absolute inset-0 bg-card rounded-md shadow-sm" layoutId="tab-pill" style={{ zIndex: -1 }} transition={{ type: 'spring', stiffness: 300, damping: 30 }} />}
		</button>
	)
}

export interface TabsContentProps {
	value: string
	lazy?: boolean
	className?: string
	children?: React.ReactNode
}export function TabsContent({ value, lazy = true, className, children, ...props }: TabsContentProps) {
	const { value: activeValue } = React.useContext(TabsContext)
	const isActive = activeValue === value
	const hasBeenActive = React.useRef(false)
	if (isActive) hasBeenActive.current = true
	if (lazy && !isActive && !hasBeenActive.current) return null
	return (
		<AnimatePresence mode="wait">
			{isActive && (
				<motion.div key={value} role="tabpanel" aria-labelledby={value} tabIndex={0} className={cn('mt-4 focus-visible:outline-none', className)} initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -6 }} transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }} suppressHydrationWarning>
					{children}
				</motion.div>
			)}
		</AnimatePresence>
	)
}
